import React from 'react';
import { Mail, Globe, MapPin, Award, Github, Linkedin, Twitter } from 'lucide-react';
import type { SocialLink } from '../../types';

const socialLinks: SocialLink[] = [
  {
    icon: Twitter,
    href: 'https://twitter.com/alex_reznik_audio',
    label: 'Follow on Twitter'
  },
  {
    icon: Linkedin,
    href: 'https://linkedin.com/in/alex-reznik',
    label: 'Connect on LinkedIn'
  },
  {
    icon: Github,
    href: 'https://github.com/alex-reznik',
    label: 'Follow on GitHub'
  },
  {
    icon: Mail,
    href: 'mailto:alex@fixphonespeakers.com',
    label: 'Email Alex'
  }
];

interface AuthorBioProps {
  variant?: 'compact' | 'full' | 'blog';
}

export function AuthorBio({ variant = 'compact' }: AuthorBioProps) {
  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-shrink-0">
          <img
            src="https://fixphonespeakers.com/alex-reznik.jpg"
            alt="Alex Reznik - Lead Mobile Audio Technician"
            className="w-32 h-32 rounded-xl object-cover"
          />
          <div className="flex justify-center gap-4 mt-4">
            {socialLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                aria-label={link.label}
              >
                <link.icon className="w-5 h-5 text-gray-400 hover:text-white" />
              </a>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="text-2xl font-bold text-white">Alex Reznik</h3>
            <p className="text-blue-400 font-medium">Lead Mobile Audio Technician & Acoustic Engineer</p>
          </div>

          {variant === 'compact' && (
            <p className="text-gray-300">
              I build tools that bring broken phone speakers back to life — without opening a single screw.
            </p>
          )}

          {variant === 'full' && (
            <>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-gray-400">
                  <MapPin className="w-4 h-4" />
                  <span>Eindhoven, Netherlands</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Award className="w-4 h-4" />
                  <span>12+ years experience</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Globe className="w-4 h-4" />
                  <span>English, Dutch</span>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-white leading-relaxed">
                  With over 12 years of experience in mobile audio diagnostics and acoustic engineering, 
                  I've dedicated my career to solving complex audio problems with elegant, user-friendly 
                  solutions. After graduating from Eindhoven University of Technology with a focus on 
                  digital signal processing, I spent five years developing diagnostic tools for major 
                  smartphone manufacturers.
                </p>
                <p className="text-white leading-relaxed">
                  My expertise lies in creating precise audio testing algorithms and developing innovative 
                  repair techniques that don't require physical intervention. I've pioneered several 
                  methods for water damage recovery in mobile speakers, leading to multiple patents in 
                  acoustic testing technology.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="font-medium text-white mb-2">Expertise</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li>Mobile Audio Diagnostics</li>
                    <li>Digital Signal Processing</li>
                    <li>Acoustic Software Development</li>
                    <li>Sound System Optimization</li>
                  </ul>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="font-medium text-white mb-2">Education</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li>MSc in Electrical Engineering</li>
                    <li>Specialization in Audio Processing</li>
                    <li>Advanced DSP Certification</li>
                    <li>Audio Engineering Society Member</li>
                  </ul>
                </div>
              </div>
            </>
          )}

          {variant === 'blog' && (
            <div>
              <p className="text-gray-300">
                Written by Alex Reznik, a mobile audio specialist with 12+ years in speaker diagnostics 
                and sound repair. Alex builds tools that fix your phone's audio without expensive repairs 
                or apps. Trust the science, hear the difference.
              </p>
              <a
                href="mailto:alex@fixphonespeakers.com"
                className="inline-flex items-center gap-2 mt-4 text-blue-400 hover:text-blue-300"
              >
                <Mail className="w-4 h-4" />
                <span>Contact Alex</span>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}